import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule, UntypedFormBuilder } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AttachmentService } from '../../../services/attachment.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/User';
import { Feedback } from '../../../models/Feedback';
import { FeedbackService } from '../../../services/feedback.service';
import { UtilsService } from '../../../services/utils.service';
import { Card } from 'primeng/card';
import { Avatar } from 'primeng/avatar';
import { FileUpload } from 'primeng/fileupload';
import { ButtonDirective } from 'primeng/button';

@Component({
  selector: 'app-give-feedback',
  templateUrl: './give-feedback.component.html',
  styleUrls: ['./give-feedback.component.scss'],

  imports: [
    Card,
    Avatar,
    TranslatePipe,
    FormsModule,
    FileUpload,
    ButtonDirective
  ]
})
export class GiveFeedbackComponent implements OnInit, OnDestroy {
  @ViewChild('uploader') uploader: any;
  private savedSuccessfullyMsg = '';
  private attachmentResult = '';

  public feedback!: Feedback;
  public checkboxLabel: string = '';
  public description: string = '';
  public title: string = '';
  public file: string = '';
  private user: User | null = null;
  public selectedFiles: File[] = [];
  public uploadedFiles: File[] = [];
  public externalFiles: any[] = [];
  public debug = '';
  public attachments!: Array<File>;
  public image: File = {} as File;
  public fileObjectUrls: { [fileName: string]: string } = {};

  private getFeedbackData(): void {
    this.authService.userProfileObservable$.subscribe((user) => {
      this.user = user;
    });

    const h = window.screen.height;
    const w = window.screen.width;

    this.feedback = {
      id: '',
      userId: this.user?.id || '',
      title: '',
      description: '',
      location: window.location.href,
      userAgent: window.navigator.userAgent,
      screenResolution: `${w} x ${h}`,
      file: '',
      status: '0',
      comment: ''
    };
  }

  constructor(
    private feedbackService: FeedbackService,
    private authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private dynamicDialogRef: DynamicDialogRef,
    private attachmentService: AttachmentService,
    private utilsService: UtilsService
  ) {
    this.savedSuccessfullyMsg = this.translateService.instant(
      'examinations.messages.examination-saved'
    );
  }

  ngOnInit(): void {
    this.getFeedbackData();
    this.checkboxLabel = this.translateService.instant('feedback.include-data');
  }

  public doClose(): void {
    this.dynamicDialogRef.close(null);
  }

  public postFeedback(): void {
    this.feedback.title = this.title;
    this.feedback.description = this.description;
    this.feedback.file = this.file;

    this.feedbackService.saveFeedback(this.feedback).subscribe((res) => {
      if (this.selectedFiles.length > 0) {
        let fd = new FormData();
        for (const single_file of this.selectedFiles) {
          fd.append('multi-files', single_file);
        }
        fd.append('documentId', res.id);
        fd.append('jiraKey', res.jiraKey || '');
        fd.append('createdBy', res.createdBy!);

        this.attachmentService.uploadFiles(fd).subscribe(
          (result) => {
            this.uploadedFiles = result;
            for (const singleFile of this.uploadedFiles) {
              this.externalFiles.push(singleFile);
            }
            // Prefetch uploaded attachments as blobs and create object URLs for secure preview
            this.prefetchAttachmentUrls(this.uploadedFiles);
            this.utilsService.showMessage(this.savedSuccessfullyMsg);
          },
          (error) => {
            this.utilsService.showError(error.message);
          }
        );
      }
      this.dynamicDialogRef.close(null);
    });
  }

  showFile(filename: string): void {
    const name = filename;
    if (this.fileObjectUrls[name]) {
      // open in new tab
      window.open(this.fileObjectUrls[name], '_blank');
      return;
    }

    // fetch blob on-demand and open
    this.attachmentService.getAttachmentBlob(name).subscribe({
      next: (blob: Blob) => {
        try {
          this.fileObjectUrls[name] = URL.createObjectURL(blob);
          window.open(this.fileObjectUrls[name], '_blank');
        } catch (e) {
          // fallback to direct download endpoint
          this.attachmentService.downloadFile(name);
        }
      },
      error: (err: any) => {
        this.utilsService.showError('Failed to load attachment');
      }
    });
  }

  private prefetchAttachmentUrls(files: any[]): void {
    if (!files || files.length === 0) return;
    for (const f of files) {
      const name = f.filename;
      if (!name) continue;
      if (this.fileObjectUrls[name]) continue;
      this.attachmentService.getAttachmentBlob(name).subscribe({
        next: (blob: Blob) => {
          try {
            this.fileObjectUrls[name] = URL.createObjectURL(blob);
          } catch (e) {
            console.error('Failed to create object URL for', name, e);
          }
        },
        error: (err: any) => {
          console.error('Prefetch failed for', name, err);
        }
      });
    }
  }

  ngOnDestroy(): void {
    try {
      for (const key of Object.keys(this.fileObjectUrls)) {
        try {
          URL.revokeObjectURL(this.fileObjectUrls[key]);
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      // ignore
    }
    this.fileObjectUrls = {};
  }

  onSelectFile(event: any, uploader: any): void {
    this.attachmentResult = this.translateService.instant(
      'healthrecords.attachments-invalidFileLimitMessageSummary'
    );
    if (event.files.length > uploader.fileLimit) {
      this.utilsService.showError(this.attachmentResult);
    } else {
      this.selectedFiles = uploader.files;
    }
  }

  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const file = new File([blob], `pasted_screenshot_${Date.now()}.png`, {
            type: blob.type
          });

          // Add to selected files
          this.selectedFiles = [...this.selectedFiles, file];

          // Update PrimeNG uploader if it exists
          if (this.uploader) {
            this.uploader.files = [...this.uploader.files, file];
            this.uploader.onSelect.emit({
              originalEvent: event,
              files: this.uploader.files,
              currentFiles: this.uploader.files
            });
          }
        }
      }
    }
  }

  onRemoveFile(event: any, uploader: any): void {
    this.selectedFiles = uploader.files;
  }

  onClearFiles(uploader: any): void {
    this.selectedFiles = [];
  }
}
